/**
 * The app shell: login, and five ways in - Capture, Glance, Browse, Search,
 * plus the detail screens they open.
 *
 * Capture is still the default tab and the camera is live immediately: the
 * target is four seconds one-handed at a till, and nothing M4 added is allowed
 * to cost that. The summary loads from IndexedDB first (open→glance under a
 * second even offline) and revalidates against the server's ETag in the
 * background - see store.ts.
 *
 * Navigation is the URL hash, because a PWA without working back-button
 * navigation feels broken on the platform it targets. Two taps from open
 * reaches any receipt or product (BUILD-SPEC 8), and back always returns
 * exactly one layer.
 */
import { render } from "preact";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";

import * as api from "./api.js";
import { Browse, type Axis } from "./browse.js";
import { Capture } from "./capture.js";
import type { Summary } from "./contracts/index.js";
import { ProductDetail, ReceiptDetail, StoreDetail } from "./detail.js";
import { Glance } from "./glance.js";
import { flush, patchSidecar, pending, remove, retryFailed, type QueueItem } from "./queue.js";
import { QueueScreen } from "./queueview.js";
import { Review } from "./review.js";
import { buildIndex, type Hit, type SearchIndex } from "./search.js";
import { SearchView } from "./searchview.js";
import * as store from "./store.js";

// ------------------------------------------------------------------ router ---

type Route =
  | { view: "capture" }
  | { view: "glance" }
  | { view: "browse"; axis: Axis }
  | { view: "search" }
  | { view: "queue" }
  | { view: "review" }
  | { view: "receipt"; id: string }
  | { view: "product"; name: string }
  | { view: "store"; name: string };

const AXES: Axis[] = ["months", "stores", "categories", "products", "receipts"];

function parseHash(hash: string): Route {
  const parts = hash.replace(/^#\/?/, "").split("/").map(decodeURIComponent);
  switch (parts[0]) {
    case "glance":
      return { view: "glance" };
    case "browse":
      return { view: "browse", axis: AXES.includes(parts[1] as Axis) ? (parts[1] as Axis) : "months" };
    case "search":
      return { view: "search" };
    case "queue":
      return { view: "queue" };
    case "review":
      return { view: "review" };
    case "receipt":
      return parts[1] ? { view: "receipt", id: parts.slice(1).join("/") } : { view: "browse", axis: "receipts" };
    case "product":
      return parts[1] ? { view: "product", name: parts[1] } : { view: "browse", axis: "products" };
    case "store":
      return parts[1] ? { view: "store", name: parts[1] } : { view: "browse", axis: "stores" };
    default:
      return { view: "capture" };
  }
}

function useRoute(): [Route, (hash: string) => void] {
  const [route, setRoute] = useState<Route>(() => parseHash(location.hash));
  useEffect(() => {
    const on = () => setRoute(parseHash(location.hash));
    addEventListener("hashchange", on);
    return () => removeEventListener("hashchange", on);
  }, []);
  const go = useCallback((hash: string) => {
    location.hash = hash;
  }, []);
  return [route, go];
}

// ------------------------------------------------------------------- queue ---

function useQueue(onDrained?: () => void) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const refresh = useCallback(async () => setItems(await pending()), []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const drain = useCallback(async () => {
    const report = await flush(api.uploadCapture);
    await refresh();
    // Something changed hands: tell the app so the server-side counts and the
    // queue screen update without a manual page refresh.
    if (report.uploaded + report.duplicates + report.failed > 0) onDrained?.();
  }, [refresh, onDrained]);

  useEffect(() => {
    // Drain on reconnect and on a timer. The timer matters because a backoff
    // that expires while the app is open should not wait for the next tap.
    const onOnline = () => void drain();
    addEventListener("online", onOnline);
    const timer = setInterval(() => {
      if (navigator.onLine) void drain();
    }, 15_000);
    return () => {
      removeEventListener("online", onOnline);
      clearInterval(timer);
    };
  }, [drain]);

  return { items, refresh, drain };
}

// ------------------------------------------------------------------- login ---

function Login({ onDone }: { onDone: (a: api.Account) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Reveal the password.
   *
   * Off by default and reset on every failure, because the reason to show it is
   * to check one you are mid-way through typing - not to leave it on screen.
   * It matters more here than on a desktop form: accounts are minted by the
   * CLI, so a first sign-in means typing sixteen random characters into a phone
   * keyboard, and the alternative to this button is guessing which character
   * went wrong and starting again.
   */
  const [reveal, setReveal] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onDone(await api.login(username, password));
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : String(err));
      setReveal(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form class="card login" onSubmit={submit}>
      <h1>Grocery</h1>
      <label>
        Username
        <input
          value={username}
          autocomplete="username"
          onInput={(e) => setUsername((e.target as HTMLInputElement).value)}
          required
        />
      </label>
      <label>
        Password
        <span class="reveal">
          <input
            type={reveal ? "text" : "password"}
            value={password}
            autocomplete="current-password"
            onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
            required
          />
          {/* Inside the label, so tapping the word "Password" still focuses the
              field rather than toggling this. */}
          <button
            type="button"
            class="link"
            aria-pressed={reveal}
            aria-label={reveal ? "Hide password" : "Show password"}
            onClick={() => setReveal((v) => !v)}
          >
            {reveal ? "Hide" : "Show"}
          </button>
        </span>
      </label>
      {error && <p class="error">{error}</p>}
      {/* The one primary action on the screen looks like it. */}
      <button type="submit" class="primary" disabled={busy}>
        {busy ? "Signing in…" : "Sign in"}
      </button>
      <p class="muted small">
        Accounts are created on the server with <code>bun run admin</code>. There is no
        sign-up, by design.
      </p>
    </form>
  );
}

// -------------------------------------------------------------------- held ---

function Held({ rows, onAnswered }: { rows: api.CaptureRow[]; onAnswered: () => void }) {
  if (!rows.length) return null;
  return (
    <section class="held">
      <h3>Needs your answer</h3>
      {rows.map((c) => {
        const open = c.answers.find((a) => !a.answer);
        return (
          <div class="card" key={c.capture_id}>
            <p>{open?.question ?? "This receipt could not be read."}</p>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const input = (e.target as HTMLFormElement).elements.namedItem(
                  "answer",
                ) as HTMLInputElement;
                if (!input.value) return;
                await api.answerCapture(c.capture_id, input.value);
                onAnswered();
              }}
            >
              <input name="answer" placeholder="Your answer" />
              <button type="submit">Send</button>
            </form>
            <button
              class="link"
              type="button"
              onClick={async () => {
                if (confirm("Discard this receipt? This cannot be undone.")) {
                  await api.discardCapture(c.capture_id);
                  onAnswered();
                }
              }}
            >
              Give up and discard
            </button>
          </div>
        );
      })}
    </section>
  );
}

// ------------------------------------------------------------------ export ---

/**
 * The workbook, demoted to an on-demand export (BUILD-SPEC 8). Fetching it
 * also keeps LibreOffice recalculation off the run's critical path - the file
 * downloaded here was built and uploaded by the last publish.
 */
function ExportRow({ enabled }: { enabled: boolean }) {
  const [problem, setProblem] = useState<string | null>(null);
  const grab = async (name: string) => {
    setProblem(null);
    try {
      await api.downloadArtifact(name);
    } catch (err) {
      setProblem(err instanceof api.ApiError ? err.message : String(err));
    }
  };
  return (
    <section class="export">
      <h3>Export</h3>
      <div class="exportbtns">
        <button type="button" disabled={!enabled} onClick={() => void grab("spend.xlsx")}>
          Workbook (.xlsx)
        </button>
        <button type="button" disabled={!enabled} onClick={() => void grab("receipts.csv")}>
          receipts.csv
        </button>
        <button type="button" disabled={!enabled} onClick={() => void grab("items.csv")}>
          items.csv
        </button>
      </div>
      {problem && <p class="error small">{problem}</p>}
      <p class="muted small">The spreadsheet is built by the run and downloaded as-is.</p>
    </section>
  );
}

// ------------------------------------------------------------------- shell ---

const NAV: { hash: string; view: Route["view"]; label: string; icon: string }[] = [
  { hash: "#/capture", view: "capture", label: "Capture", icon: "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm-8-2h3l2-2h6l2 2h3a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z" },
  { hash: "#/glance", view: "glance", label: "Glance", icon: "M4 19V10m5.5 9V5M15 19v-6m5.5 6V8" },
  { hash: "#/browse", view: "browse", label: "Browse", icon: "M4 6h16M4 12h16M4 18h10" },
  { hash: "#/search", view: "search", label: "Search", icon: "M10.5 4a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13Zm9.5 16-4.4-4.4" },
  { hash: "#/review", view: "review", label: "Review", icon: "M9 6h11M9 12h11M9 18h11M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2" },
];

const TITLE: Record<Route["view"], string> = {
  capture: "Capture",
  glance: "Glance",
  browse: "Browse",
  search: "Search",
  queue: "Queue",
  review: "Review",
  receipt: "Receipt",
  product: "Product",
  store: "Store",
};

function App() {
  const [account, setAccount] = useState<api.Account | null>(null);
  const [route, go] = useRoute();
  const [stores, setStores] = useState<string[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [captures, setCaptures] = useState<api.CaptureRow[]>([]);
  const [equivEdits, setEquivEdits] = useState<api.EquivalentEdit[]>([]);
  const [metaEdits, setMetaEdits] = useState<api.ProductMetaEdit[]>([]);
  const [serverQueue, setServerQueue] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);

  // A stable callback into the *current* load, so drains can refresh server
  // state without re-wiring the queue's timers on every render.
  const loadRef = useRef<() => void>(() => undefined);
  const onDrained = useCallback(() => loadRef.current(), []);
  const { items, refresh, drain } = useQueue(onDrained);

  // Resume a session on reload without making the user sign in again.
  useEffect(() => {
    if (!api.getToken()) return;
    api
      .whoami()
      .then((w) =>
        setAccount({
          id: w.account_id,
          username: w.username,
          timezone: w.timezone,
          must_change_password: false,
        }),
      )
      .catch(() => api.setToken(null));
  }, []);

  /** Cache first, then the network's opinion. See store.ts. */
  const loadSummary = useCallback(() => {
    if (!account) return;
    void store.loadSummary(api.summaryConditional, (s, source) => {
      setSummary(s);
      if (s && source === "network") {
        // Warm the photo cache for the receipts most likely to be opened.
        const ids = s.receipts
          .map((r) => r.capture_id)
          .filter((id): id is string => typeof id === "string" && id.length > 0);
        void store.prefetchPhotos(ids, api.capturePhoto);
      }
    });
  }, [account]);

  const load = useCallback(async () => {
    if (!account) return;
    loadSummary();
    try {
      const [cfg, view, eq, meta] = await Promise.all([
        // A missing config must not stop capture working. Falling back to a
        // free-text store is exactly the new-shop case the server is built to
        // accept, so degrading here costs nothing.
        api.config().catch(() => ({ config: {} as { stores?: string[] }, rules_md: "" })),
        api.captures(),
        api.equivalentEdits().catch(() => ({ edits: [] as api.EquivalentEdit[] })),
        api.productMetaEdits().catch(() => ({ edits: [] as api.ProductMetaEdit[] })),
      ]);
      // Empty, not ["unknown"]: with Other… selected by default an "unknown"
      // chip beside it would be a second control for the same state, both lit.
      setStores(cfg.config.stores ?? []);
      setServerQueue(view.queue.pending);
      setCaptures(view.captures);
      setEquivEdits(eq.edits);
      setMetaEdits(meta.edits);
      setProblem(null);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    }
  }, [account, loadSummary]);

  useEffect(() => {
    loadRef.current = () => void load();
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  // Re-validate when arriving on a data screen: almost always a free 304.
  useEffect(() => {
    if (route.view !== "capture") loadSummary();
  }, [route.view, loadSummary]);

  // Every screen opens at its top. Without this, the scroll position of the
  // last screen leaks into the next one - most visibly a long Browse list
  // opening a detail page already half-way down.
  useEffect(() => {
    scrollTo(0, 0);
  }, [route]);

  const searchIndex: SearchIndex | null = useMemo(
    () => (summary ? buildIndex(summary) : null),
    [summary],
  );

  if (!account) return <Login onDone={setAccount} />;

  const localDepth = items.length;
  const depth = localDepth + serverQueue;

  const openReceipt = (id: string) => go(`#/receipt/${encodeURIComponent(id)}`);
  const openProduct = (name: string) => go(`#/product/${encodeURIComponent(name)}`);
  const openStore = (name: string) => go(`#/store/${encodeURIComponent(name)}`);
  const back = () => history.back();

  const onHit = (hit: Hit) => {
    if (hit.kind === "product") openProduct(hit.key);
    else if (hit.kind === "store") openStore(hit.key);
    else openReceipt(hit.key);
  };

  const held = captures.filter((c) => c.status === "held");

  const reviewOpen = (summary?.meta_suggestions ?? []).filter(
    (sug) => !metaEdits.some((e) => e.product.trim().toLowerCase() === sug.product.trim().toLowerCase()),
  ).length;

  const navView: Route["view"] =
    route.view === "receipt" || route.view === "product" || route.view === "store"
      ? "browse"
      : route.view === "queue"
        ? "capture"
        : route.view;

  return (
    <div class="app">
      <header>
        <span class="apptitle">{TITLE[route.view]}</span>
        {/* The one number that says whether a PC run is worth doing - and now
            also the door to the queue it counts. */}
        <button class="depth" type="button" title="View the queue" onClick={() => go("#/queue")}>
          {depth}
        </button>
        <button
          class="link signout"
          type="button"
          onClick={async () => {
            await api.logout();
            setAccount(null);
          }}
        >
          Sign out
        </button>
      </header>

      {problem && <p class="error banner">{problem}</p>}

      <main>
        {route.view === "capture" && (
          <>
            <Capture
              stores={stores}
              queueItems={items}
              onCaptured={() => {
                void refresh();
                void drain();
              }}
            />
            {depth > 0 && (
              <button class="queuelink" type="button" onClick={() => go("#/queue")}>
                {depth} in the queue - view & manage ›
              </button>
            )}
            <Held
              rows={held}
              onAnswered={() => {
                void load();
              }}
            />
          </>
        )}
        {route.view === "queue" && (
          <QueueScreen
            local={items}
            server={captures}
            actions={{
              discardLocal: async (id) => {
                await remove(id);
                await refresh();
              },
              retryLocal: async (id) => {
                await retryFailed(id);
                await refresh();
                void drain();
              },
              discardServer: async (id) => {
                await api.discardCapture(id);
                await load();
              },
              answerServer: async (id, answer) => {
                await api.answerCapture(id, answer);
                await load();
              },
              setServerTotal: async (id, total) => {
                await api.updateCaptureTotal(id, total);
                await load();
              },
              setLocalTotal: async (id, total) => {
                await patchSidecar(id, { card_total: total });
                await refresh();
              },
            }}
          />
        )}
        {route.view === "glance" && (
          <>
            <Glance summary={summary} queueDepth={depth} onQueueTap={() => go("#/queue")} />
            <ExportRow enabled={summary !== null} />
          </>
        )}
        {route.view === "browse" && (
          <Browse
            summary={summary}
            axis={route.axis}
            onAxis={(a) => go(`#/browse/${a}`)}
            nav={{ openReceipt, openProduct, openStore, openQueue: () => go("#/queue") }}
            queueDepth={depth}
          />
        )}
        {route.view === "search" && <SearchView index={searchIndex} onHit={onHit} />}
        {route.view === "review" && (
          <Review
            summary={summary}
            edits={metaEdits}
            onDecide={async (edit) => {
              const res = await api.saveProductMetaEdit(edit);
              setMetaEdits(res.edits);
            }}
          />
        )}
        {route.view === "receipt" &&
          (summary ? (
            <ReceiptDetail summary={summary} id={route.id} onBack={back} onOpenProduct={openProduct} />
          ) : (
            <p class="muted pad">Nothing published yet.</p>
          ))}
        {route.view === "product" &&
          (summary ? (
            <ProductDetail
              summary={summary}
              name={route.name}
              onBack={back}
              onOpenStore={openStore}
              equivEdits={equivEdits}
              onEquivEdit={async (edit) => {
                const res = await api.saveEquivalentEdit(edit);
                setEquivEdits(res.edits);
              }}
            />
          ) : (
            <p class="muted pad">Nothing published yet.</p>
          ))}
        {route.view === "store" &&
          (summary ? (
            <StoreDetail summary={summary} name={route.name} onBack={back} onOpenReceipt={openReceipt} />
          ) : (
            <p class="muted pad">Nothing published yet.</p>
          ))}
      </main>

      <nav class="tabbar" aria-label="Sections">
        {NAV.map((n) => (
          <button
            key={n.view}
            type="button"
            class={navView === n.view ? "on" : ""}
            aria-current={navView === n.view ? "page" : undefined}
            onClick={() => go(n.hash)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d={n.icon} />
            </svg>
            <span>{n.label}</span>
            {n.view === "capture" && depth > 0 && <span class="badge">{depth}</span>}
            {n.view === "review" && reviewOpen > 0 && <span class="badge">{reviewOpen}</span>}
          </button>
        ))}
      </nav>
    </div>
  );
}

render(<App />, document.getElementById("root")!);

// Registered after first paint so it never delays the camera.
if ("serviceWorker" in navigator) {
  addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").then((reg) => {
      reg.addEventListener("updatefound", () => {
        const next = reg.installing;
        next?.addEventListener("statechange", () => {
          // A stale service worker is the classic way a PWA becomes unfixable,
          // so a new one announces itself rather than waiting for a cold start.
          if (next.state === "installed" && navigator.serviceWorker.controller) {
            const bar = document.createElement("button");
            bar.className = "update-toast";
            bar.textContent = "Update available - tap to refresh";
            bar.onclick = () => location.reload();
            document.body.appendChild(bar);
          }
        });
      });
    });
  });
}
